import {useMutation, useQuery} from '@apollo/client/react';

import {graphql} from '../gqlApp';
import {getToken} from '../utils/auth';
import {GET_COMPANY, GET_MY_COMPANIES} from './company';
import {API_BASE} from 'src/api/endpoints';

graphql(`
	fragment OfferVersionFields on OfferVersionModel {
		id
		version
		title
		changeType
		summary
		sizeBytes
		checksum
		publishedAt
		effectiveFrom
	}
`);

export const GET_COMPANY_OFFER = graphql(`
	query CompanyOffer($companyId: ID!) {
		companyOffer(companyId: $companyId) {
			needsAcceptance
			acceptable
			overdue
			deadline
			current {
				...OfferVersionFields
			}
			accepted {
				...OfferVersionFields
			}
			history {
				id
				method
				acceptedAt
				documentChecksum
				offerVersion {
					...OfferVersionFields
				}
			}
		}
	}
`);

export const useCompanyOffer = (companyId: string | undefined) =>
	useQuery(GET_COMPANY_OFFER, {variables: {companyId: companyId ?? ''}, skip: !companyId});

/**
 * Шаг 1 из 2: запросить SMS-код на телефон владельца (SKS-506).
 */
export const REQUEST_OFFER_ACCEPTANCE_CODE = graphql(`
	mutation RequestOfferAcceptanceCode($input: RequestOfferAcceptanceCodeInput!) {
		requestOfferAcceptanceCode(input: $input) {
			channel
			resendTimeout
			devCode
		}
	}
`);

export const useRequestOfferAcceptanceCode = () =>
	useMutation(REQUEST_OFFER_ACCEPTANCE_CODE);

export const ACCEPT_OFFER = graphql(`
	mutation AcceptOffer($input: AcceptOfferInput!) {
		acceptOffer(input: $input) {
			needsAcceptance
			acceptable
			overdue
			deadline
			current {
				...OfferVersionFields
			}
			accepted {
				...OfferVersionFields
			}
			history {
				id
				method
				acceptedAt
				documentChecksum
				offerVersion {
					...OfferVersionFields
				}
			}
		}
	}
`);

/**
 * После принятия перезапрашиваем и компанию со списком, и саму оферту.
 *
 * Компанию — потому что принятие двигает статус (AWAITING_OFFER →
 * AWAITING_BANK_CHECK) или снимает приостановку, а статус показан в шапке
 * и в карточке; считать новый статус на клиенте значило бы держать конвейер
 * в двух местах.
 *
 * Состояние оферты — потому что смержить его из ответа мутации нельзя:
 * у CompanyOfferState нет идентификатора, и нормализованный кэш оставляет
 * прежнее значение. Без этого экран после принятия показывает «Требует принятия».
 */
export const useAcceptOffer = (companyId: string | undefined) =>
	useMutation(ACCEPT_OFFER, {
		/*
		 * Запросы перечисляем с переменными, а не одними документами: обновление
		 * по документу перезапрашивает все экземпляры, включая пропущенные. Меню
		 * и баннер зовут companyOffer всегда, и пока компания не выбрана, у них
		 * в переменных пустая строка при skip — рефетч снял бы skip и отправил её
		 * на сервер, где она падает на разборе uuid.
		 */
		refetchQueries: companyId
			? [
					{query: GET_COMPANY_OFFER, variables: {companyId}},
					{query: GET_COMPANY, variables: {id: companyId}},
					GET_MY_COMPANIES,
				]
			: [],
		awaitRefetchQueries: true,
	});

/**
 * Скачать редакцию файлом.
 *
 * Через fetch и временную ссылку, а не `<a href>`: эндпоинт закрыт guard'ом,
 * который читает токен только из заголовка Authorization.
 */
export async function downloadOfferPdf(offerVersionId: string, fileName: string): Promise<void> {
	const url = await fetchOfferPdfUrl(offerVersionId);
	const link = document.createElement('a');

	link.href = url;
	link.download = fileName;

	document.body.append(link);
	link.click();
	link.remove();

	setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Blob-ссылка на PDF. Освободить её обязан вызывающий (URL.revokeObjectURL). */
export async function fetchOfferPdfUrl(offerVersionId: string): Promise<string> {
	const response = await fetch(`${API_BASE}/api/offers/${offerVersionId}/file`, {
		headers: {Authorization: `Bearer ${getToken() ?? ''}`},
	});

	if (!response.ok) {
		throw new Error('Не удалось загрузить PDF оферты');
	}

	return URL.createObjectURL(await response.blob());
}
